import { BROWSER_ERROR_KEYS, instrumentBrowserError } from '@library/observability/browser'
import React, { type ComponentType, useEffect, useMemo } from 'react'
import type { z } from 'zod'

import { Carousel, CarouselFieldsSchema } from '../blocks/Carousel'
import { FAQs, FaqsFieldsSchema } from '../blocks/FAQs'
import { Hero, HeroFieldsSchema } from '../blocks/Hero'
import { ImageTextColumns, ImageTextColumnsFieldsSchema } from '../blocks/ImageTextColumns'
import { StepsWithImage, StepsWithImageFieldsSchema } from '../blocks/StepsWithImage'
import { Table, TableFieldsSchema } from '../blocks/Table'
import { Testimonials, TestimonialsFieldsSchema } from '../blocks/Testimonials'
import type { CmsCtaIntentHandlers, OnTriggerPromoCode } from '../cta'
import { SafeBlock } from './SafeBlock'
import type { CmsPageFields, CmsPageSection } from './types'

type BlockDefinition = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>
  schema: z.ZodTypeAny
}

// Block content-type uid → its React component and Zod validator. A single
// registry keeps the component and its schema in lockstep — a block can't be
// wired up with one but not the other. Unknown UIDs are silently skipped.
const BLOCK_REGISTRY: Record<string, BlockDefinition> = {
  carousel: { Component: Carousel, schema: CarouselFieldsSchema },
  faqs: { Component: FAQs, schema: FaqsFieldsSchema },
  hero: { Component: Hero, schema: HeroFieldsSchema },
  image_text_columns: { Component: ImageTextColumns, schema: ImageTextColumnsFieldsSchema },
  steps_with_image: { Component: StepsWithImage, schema: StepsWithImageFieldsSchema },
  table: { Component: Table, schema: TableFieldsSchema },
  testimonials: { Component: Testimonials, schema: TestimonialsFieldsSchema },
}

type Props = {
  page: CmsPageFields
  ctaIntentHandlers?: CmsCtaIntentHandlers
  onTriggerPromocode?: OnTriggerPromoCode
}

type ValidatedEntry = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>
  fields: Record<string, unknown>
  blockKey: string
}

type ValidationError = {
  blockUid: string
  sectionIndex: number
  issues: Array<{ path: Array<string | number>; message: string; code: string }>
}

export function PageRenderer({
  page,
  ctaIntentHandlers,
  onTriggerPromocode,
}: Props): JSX.Element | null {
  // Pure derivation only — telemetry is fired from the effect below so that
  // renders React discards (concurrent mode) or replays (StrictMode) never
  // emit phantom or duplicated validation errors.
  const { entries, errors } = useMemo<{
    entries: Array<ValidatedEntry | null>
    errors: ValidationError[]
  }>(() => {
    if (!page.sections || page.sections.length === 0) return { entries: [], errors: [] }

    const validationErrors: ValidationError[] = []

    const validatedEntries = page.sections.map((section: CmsPageSection, index) => {
      const blockUid = Object.keys(section)[0]
      if (!blockUid) return null

      const block = BLOCK_REGISTRY[blockUid]
      if (!block) return null
      const { Component, schema } = block

      const payload = (section as Record<string, unknown>)[blockUid]
      const result = schema.safeParse(payload)
      if (!result.success) {
        validationErrors.push({
          blockUid,
          sectionIndex: index,
          issues: result.error.issues.map((i) => ({
            path: i.path,
            message: i.message,
            code: i.code,
          })),
        })
        return null
      }

      return {
        Component,
        fields: result.data as Record<string, unknown>,
        blockKey: `${blockUid}-${index}`,
      }
    })

    return { entries: validatedEntries, errors: validationErrors }
  }, [page.sections])

  useEffect(() => {
    errors.forEach((error) => {
      instrumentBrowserError(BROWSER_ERROR_KEYS.CMS_BLOCK_VALIDATION_ERROR, error)
    })
  }, [errors])

  if (entries.length === 0) return null

  return (
    <>
      {entries.map((entry) =>
        entry ? (
          <SafeBlock key={entry.blockKey} blockKey={entry.blockKey}>
            <entry.Component
              {...entry.fields}
              ctaIntentHandlers={ctaIntentHandlers}
              onTriggerPromocode={onTriggerPromocode}
            />
          </SafeBlock>
        ) : null,
      )}
    </>
  )
}
